import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, Space, message, Tag, Row, Col, Spin } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, EyeOutlined, FileTextOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { ensureUploadMode, getUploadMode } from '../utils/uploadMode';
import { downloadOssFile, previewOssPdf } from '../utils/ossUpload';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

// 将探头外形尺寸从存储格式（高*长*宽）转换为显示格式（长*宽*高）
const formatProbeOuterSize = (text) => {
  if (!text || text === '-') return text;
  const parts = text.split('*');
  if (parts.length !== 3) return text;
  // 存储格式: height*length*width
  // 显示格式: length*width*height
  return `${parts[1]}*${parts[2]}*${parts[0]}`;
};

const { Content } = Layout;

const DrawingDetailPage = () => {
  const navigate = useNavigate();
  const { deviceType, id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isDarkMode, getBackgroundColor, getContentBg, getTextColor, getTextColorSecondary, getBorderColor } = useTheme();

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/drawings/${deviceType}/${id}`);
      setData(res.data);
      
      if (user.role !== 'sales') {
        try {
          const statusRes = await api.get(`/drawing-approvals/download-3d-status/${deviceType}/${id}`);
          setApprovalStatus(statusRes.data);
        } catch (e) {
          setApprovalStatus({ hasApproval: false });
        }
      }
    } catch (err) {
      message.error('获取详情失败');
    } finally {
      setLoading(false);
    }
  }, [deviceType, id, user.role]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleDownload = async (fileType) => {
    if (fileType === 'three_d_stp') {
      if (approvalStatus?.downloaded) {
        handleRequest3DApproval();
        return;
      }
      if (!approvalStatus?.hasApproval || approvalStatus?.status !== 'approved') {
        message.warning('3D图下载需要审批，请先提交下载申请');
        return;
      }
    }
    
    try {
      const fileName = data[`${fileType}_name`] || `${data.drawing_no}_${fileType}`;
      await ensureUploadMode();
      if (getUploadMode() === 'oss') {
        await downloadOssFile(`/drawings/${deviceType}/${id}/download/${fileType}`, fileName);
      } else {
        const res = await api.get(`/drawings/${deviceType}/${id}/download/${fileType}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || '下载失败';
      message.error(errMsg);
    }
  };

  const handlePreview = async (fileType) => {
    if (fileType === 'three_d_stp') {
      message.info('STP文件不支持浏览器预览，请下载后使用专业软件查看');
      return;
    }
    
    const fileName = data[`${fileType}_name`] || '';
    const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
    
    if (ext === '.pdf') {
      try {
        await ensureUploadMode();
        if (getUploadMode() === 'oss') {
          await previewOssPdf(`/drawings/${deviceType}/${id}/preview/${fileType}`);
        } else {
          const response = await api.get(`/drawings/${deviceType}/${id}/preview/${fileType}`, { responseType: 'blob' });
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 30000);
        }
      } catch (err) {
        message.error('预览失败');
      }
      return;
    }

    message.info('该文件格式不支持浏览器预览，请下载后使用专业软件查看');
  };

  const handleRequest3DApproval = async () => {
    try {
      const res = await api.post(`/drawing-approvals/download-3d/${deviceType}/${id}`);
      message.success(res.data.message || '已提交3D图下载审批申请');
      setApprovalStatus({ hasApproval: true, status: 'pending' });
    } catch (err) {
      message.error(err.response?.data?.message || '提交失败');
    }
  };

  const renderFileSection = (fileType, label) => {
    const fileField = `${fileType}_file`;
    const nameField = `${fileType}_name`;
    const hasFile = !!data[fileField];
    const fileName = data[nameField] || '';
    if (!hasFile) return null;

    const is3D = fileType === 'three_d_stp';
    const needsApproval = is3D && (!approvalStatus?.hasApproval || approvalStatus?.status !== 'approved' || approvalStatus?.downloaded);
    const isPending = is3D && approvalStatus?.status === 'pending';
    
    const ext = (fileName || '').toLowerCase();
    const isPdf = ext.endsWith('.pdf');
    const canPreview = isPdf;

    return (
      <div 
        key={fileType} 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 0',
          borderBottom: `1px solid ${getBorderColor()}` 
        }}
      >
        <span style={{ color: getTextColorSecondary(), fontSize: 14, minWidth: 100 }}>{label}</span>
        <Space>
          <span style={{ color: getTextColor(), fontSize: 14, marginRight: 12 }}>{fileName}</span>
          {needsApproval ? (
            <Button 
              size="small" 
              type="primary" 
              onClick={handleRequest3DApproval}
            >
              申请下载审批
            </Button>
          ) : (
            <>
              <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(fileType)}>下载</Button>
              {canPreview && (
                <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(fileType)}>预览</Button>
              )}
              {is3D && <Tag color="green">已审批</Tag>}
            </>
          )}
          {isPending && <Tag color="orange">审批中</Tag>}
        </Space>
      </div>
    );
  };

  const InfoItem = ({ label, value }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: getTextColorSecondary(), fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ color: getTextColor(), fontSize: 14, fontWeight: 500 }}>{value || '-'}</div>
    </div>
  );

  const SizeItem = ({ label, value, unit }) => (
    <div 
      style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '14px 0',
        borderBottom: `1px solid ${getBorderColor()}`,
        minHeight: 48
      }}
    >
      <span style={{ color: getTextColorSecondary(), fontSize: 13 }}>{label}</span>
      <span style={{ color: getTextColor(), fontSize: 14, fontWeight: 600 }}>
        {value || '-'}{unit ? <span style={{ fontWeight: 'normal', marginLeft: 4, color: getTextColorSecondary() }}>{unit}</span> : ''}
      </span>
    </div>
  );

  const handleBack = () => {
    const backParams = searchParams.toString();
    if (backParams) {
      navigate(`/drawings/${deviceType}?${backParams}`);
    } else {
      navigate(`/drawings/${deviceType}`);
    }
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
        <AppHeader />
        <Content style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
        <AppHeader />
        <Content style={{ padding: 24, color: getTextColor() }}>加载中...</Content>
      </Layout>
    );
  }

  const holeDisplay = data.holePosition 
    ? `${data.holePosition.hole_spacing_length}*${data.holePosition.hole_spacing_width}mm*${data.holePosition.hole_diameter}`
    : null;

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            style={{ marginRight: 16 }}
          >
            返回列表
          </Button>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: getTextColor() }}>
              图纸详情 - {data.drawing_no}
            </h1>
            <p style={{ margin: 0, marginTop: 4, fontSize: 13, color: getTextColorSecondary() }}>
              皮带式金属检测机图纸
            </p>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <div style={{ 
              background: getContentBg(), 
              borderRadius: 8, 
              border: `1px solid ${getBorderColor()}`, 
              overflow: 'hidden' 
            }}>
              <div style={{ 
                padding: '16px 20px', 
                borderBottom: `1px solid ${getBorderColor()}`,
                display: 'flex',
                alignItems: 'center'
              }}>
                <FileTextOutlined style={{ color: '#1890ff', marginRight: 8, fontSize: 16 }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: getTextColor() }}>基本信息</span>
              </div>
              
              <div style={{ padding: '20px' }}>
                <Row gutter={[32, 0]}>
                  <Col span={12}>
                    <InfoItem label="图纸编号" value={data.drawing_no} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="生产通知单" value={data.production_notice} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="制图人" value={data.creator} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="制图日期" value={data.draw_date} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="机架类型" value={data.frame_type} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="探头类型" value={data.probe_type} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="探头开口尺寸" value={data.probe_size} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="皮带厚度" value={data.belt_thickness ? `${data.belt_thickness}mm` : null} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="皮带材质" value={data.belt_material} />
                  </Col>
                  <Col span={12}>
                    <InfoItem label="皮带尺寸" value={data.belt_size} />
                  </Col>
                </Row>

                <div style={{ 
                  marginTop: 8, 
                  paddingTop: 16, 
                  borderTop: `1px solid ${getBorderColor()}` 
                }}>
                  <div style={{ color: getTextColorSecondary(), fontSize: 12, marginBottom: 4 }}>其他特点</div>
                  <div style={{ color: getTextColor(), fontSize: 14, fontWeight: 500, lineHeight: 1.6, minHeight: 20 }}>
                    {data.other_features && data.other_features !== '-' ? data.other_features : '-'}
                  </div>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={12}>
            <div style={{ 
              background: getContentBg(), 
              borderRadius: 8, 
              border: `1px solid ${getBorderColor()}`, 
              overflow: 'hidden' 
            }}>
              <div style={{ 
                padding: '16px 20px', 
                borderBottom: `1px solid ${getBorderColor()}`,
                display: 'flex',
                alignItems: 'center'
              }}>
                <ContainerOutlined style={{ color: '#1890ff', marginRight: 8, fontSize: 16 }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: getTextColor() }}>尺寸信息</span>
              </div>
              
              <div style={{ padding: '8px 20px 12px' }}>
                <SizeItem label="可通过尺寸" value={data.passable_size} unit="mm" />
                <SizeItem label="探头外形尺寸" value={formatProbeOuterSize(data.probe_outer_size)} unit="mm" />
                <SizeItem label="整机外形尺寸" value={data.machine_outer_size} unit="mm" />
                <SizeItem label="机架长度" value={data.frame_length} unit="mm" />
                <SizeItem label="皮带输送面离地高度" value={data.belt_height} unit="mm" />
                <SizeItem label="皮带运行方向" value={data.belt_direction} />
                <SizeItem label="控制箱与机身设计" value={data.control_box_design} />
                <SizeItem label="报警剔除方式" value={data.alarm_reject_mode} />
                {holeDisplay && (
                  <SizeItem label="安装孔位" value={holeDisplay} />
                )}
              </div>
            </div>
          </Col>
        </Row>

        <div style={{ 
          marginTop: 24, 
          background: getContentBg(), 
          borderRadius: 8, 
          border: `1px solid ${getBorderColor()}`,
          overflow: 'hidden' 
        }}>
          <div style={{ 
            padding: '16px 20px', 
            borderBottom: `1px solid ${getBorderColor()}` 
          }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: getTextColor() }}>文件列表</span>
          </div>
          <div style={{ padding: '8px 20px 12px' }}>
            {renderFileSection('scheme_pdf', '方案图PDF')}
            {renderFileSection('scheme_dwg', '方案图DWG')}
            {renderFileSection('sheet_dwg', '钣金图DWG')}
            {renderFileSection('machining_dwg', '机加工DWG')}
            {renderFileSection('frame_dwg', '架子图DWG')}
            {renderFileSection('probe_dwg', '探头图DWG')}
            {renderFileSection('three_d_stp', '3D图')}
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default DrawingDetailPage;
